// Seletor de modelo em modal de tamanho fixo: busca no topo (ilike — contém, sem diferenciar
// maiúsculas nem acentos) e a lista rola só por dentro. Setas navegam, Enter escolhe, Esc fecha.
// Aba "Padrão" (camada 1 do quadro): Izze · comerciais mais recentes · "Ver mais" (resto dos comerciais e modelos do escopo).
// Aba "Modo Turbo" (se ligado em Configurações): famílias × faixas Small/Medium/High, com ★ no recomendado e a disponibilidade.
// Aba "Modo Desenvolvedor" (se ligado, em pool dedicada ou on-premise): modelo específico entre os instalados.
// A fonte é a lista do escopo ativo (scope.tsx → GET /api/client/models); na Community, enquanto ela não chega, a lista global de `me`.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { brl, int, type BandId, type FamilyBands, type InstalledModel, type Me, type Scope } from '../api';
import { LABELS } from '../labels';
import { allItems, bandLabel, bandValue, buildCatalog, fold, isHidden, parseBand, sourceFromMe, type Catalog, type CatalogItem, type CatalogSource } from '../models/catalog';
import { isSpecificScope, type ScopeModels } from '../scope';
import '../styles/model-picker.css';

// Linha navegável: item escolhível ou o botão "Ver mais". O índice na lista achatada é o `data-i` / `mp-opt-<i>`.
type Opt = { kind: 'opt'; item: CatalogItem };
type Row = Opt | { kind: 'more' };
type Section = { key: string; title?: string; rows: Opt[]; empty?: string; start: number };
type Tab = 'default' | 'turbo' | 'dev';
const hasOpt = (secs: Section[]) => secs.some((x) => x.rows.length > 0);

const T_COMMERCIAL = 'Comerciais · rodam fora da rede';
const IZZE_HINT = 'escolhe o modelo por você';
const EMPTY: CatalogSource = { models: [], openrouter: [] };
const noun = (n: number) => (n === 1 ? 'modelo' : 'modelos');
const machines = (n: number) => `${n} ${n === 1 ? 'máquina' : 'máquinas'}`;
const fmtSize = (b: number | null) => (b == null ? '—' : b >= 1e9 ? `${(b / 1e9).toFixed(1).replace('.', ',')} GB` : `${Math.max(1, Math.round(b / 1e6))} MB`);
const BAND_ORDER: BandId[] = ['small', 'medium', 'high'];

/**
 * Monta as seções e a lista achatada das linhas visíveis. `head` vem antes do botão "Ver mais", `tail` depois (só expandido).
 * Dentro do "Ver mais" os modelos da rede vêm antes dos demais comerciais (o quadro: o "ver mais" abre o open source local).
 * Com busca, filtra tudo na mesma ordem e não há botão (o recolhimento é ignorado). Fora da Community (`flat`) não há comerciais
 * a esconder: os modelos do escopo aparecem direto, sem botão.
 */
function layout(cat: Catalog, query: string, expanded: boolean): { head: Section[]; tail: Section[]; rows: Row[] } {
  const q = fold(query.trim());
  const keep = (list: CatalogItem[]) => (q ? list.filter((i) => i.hay.includes(q)) : list);
  const opts = (list: CatalogItem[]): Opt[] => list.map((item) => ({ kind: 'opt', item }));
  const hasOthers = cat.featured.length + cat.moreCommercial.length + cat.open.length > 0;
  const head: Section[] = [], tail: Section[] = [];
  let start = 0; // índice da 1ª linha da seção na lista achatada
  const add = (into: Section[], s: Omit<Section, 'start'>) => { if (!s.rows.length && !s.empty) return; into.push({ ...s, start }); start += s.rows.length; };
  const sections = (into: Section[]) => {
    const open = keep(cat.open), more = keep(cat.moreCommercial);
    add(into, { key: 'open', title: `${cat.openTitle} (${open.length})`, rows: opts(open), empty: !q && !open.length ? cat.openEmpty : undefined });
    add(into, { key: 'more-commercial', title: `Outros comerciais (${more.length})`, rows: opts(more) });
  };

  add(head, { key: 'izze', rows: opts(keep([cat.izze, ...(cat.orphan ? [cat.orphan] : [])])), empty: !q && !hasOthers && !cat.flat ? 'Nenhum outro modelo disponível agora' : undefined });
  add(head, { key: 'featured', title: T_COMMERCIAL, rows: opts(keep(cat.featured)) });
  let more = false;
  if (q || cat.flat) sections(head);
  else if (hasOthers) {
    more = true; start += 1; // o botão ocupa um índice
    if (expanded) sections(tail);
  }
  const rows: Row[] = [...head.flatMap((x) => x.rows), ...(more ? [{ kind: 'more' } as Row] : []), ...tail.flatMap((x) => x.rows)];
  return { head, tail, rows };
}

export function ModelPicker({ me, value, onChange, scope, models }: { me: Me; value: string; onChange: (id: string) => void; scope: Scope | null; models: ScopeModels }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<Tab>('default');
  const [onlyOk, setOnlyOk] = useState(true);   // Modo Desenvolvedor: Recomendados | Todos
  const [adv, setAdv] = useState(false);        // Modo Desenvolvedor: Simples | Avançado
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef(false);

  const kind = scope?.kind ?? 'community';
  const data = models.data;
  const turboOn = me.prefs?.turbo === true;
  const devOn = me.prefs?.developer === true && isSpecificScope(scope);
  const tabs = useMemo(() => [{ id: 'default' as Tab, label: 'Padrão' }, ...(turboOn ? [{ id: 'turbo' as Tab, label: LABELS.turbo }] : []), ...(devOn ? [{ id: 'dev' as Tab, label: LABELS.developer }] : [])], [turboOn, devOn]);
  const cur: Tab = tabs.some((t) => t.id === tab) ? tab : 'default';   // a aba que sumiu (Turbo desligado, escopo trocado) volta ao padrão
  const blocked = kind !== 'community' && !data;   // sem a lista do escopo (carregando ou falhou): só o Izze, que vale em qualquer escopo

  const catalog = useMemo(() => buildCatalog(data ?? (kind === 'community' ? sourceFromMe(me) : EMPTY), value, { kind, loading: blocked }), [data, me, kind, value, blocked]);
  const items = useMemo(() => allItems(catalog), [catalog]);
  const current = items.find((i) => i.id === value);
  const { head, tail, rows } = useMemo(() => layout(catalog, query, expanded), [catalog, query, expanded]);
  const moreIdx = rows.findIndex((r) => r.kind === 'more');

  // Modo Turbo: famílias com ao menos uma faixa; Modo Desenvolvedor: o que as máquinas do escopo têm instalado
  const q = fold(query.trim());
  const fams = useMemo(() => (data?.bands ?? []).filter((f) => !q || fold(f.family).includes(q)), [data, q]);
  const installed = data?.installed;
  const devRows = useMemo(
    () => (installed ?? []).filter((m) => (!onlyOk || m.qualified) && (!q || fold(m.id).includes(q))).sort((a, b) => Number(b.qualified) - Number(a.qualified) || a.id.localeCompare(b.id)),
    [installed, onlyOk, q],
  );
  const navRows = cur === 'default' ? rows : cur === 'dev' ? devRows : [];

  const show = () => {
    // abre com "Ver mais" já expandido quando o modelo atual está lá dentro, senão ele ficaria escondido
    const exp = isHidden(catalog, value);
    setQuery('');
    setExpanded(exp);
    setTab(turboOn && parseBand(value) ? 'turbo' : 'default');
    setActive(Math.max(0, layout(catalog, '', exp).rows.findIndex((r) => r.kind === 'opt' && r.item.id === value)));
    centerRef.current = true;
    setOpen(true);
    models.reload();   // a lista do escopo muda sem a pessoa fazer nada: abre já com a mais recente
  };
  const close = () => { setOpen(false); triggerRef.current?.focus(); };
  const pick = (i: CatalogItem | undefined) => { if (!i) return; onChange(i.id); close(); };
  const pickBand = (f: FamilyBands, id: BandId) => { if (!f.bands.find((b) => b.id === id)?.available) return; onChange(bandValue(f.family, id)); close(); };
  const pickDev = (m: InstalledModel | undefined) => { if (!m || !m.qualified) return; onChange(m.id); close(); };
  // clique fora do painel fecha; o preventDefault impede o foco padrão do mousedown de cair no <body> depois do focus() do close()
  const onOverlayDown = (e: MouseEvent<HTMLDivElement>) => { if (e.target !== e.currentTarget) return; e.preventDefault(); close(); };
  const toggleMore = () => { setExpanded((x) => !x); if (moreIdx >= 0) setActive(moreIdx); };
  const activate = (r: Row | undefined) => { if (!r) return; if (r.kind === 'more') toggleMore(); else pick(r.item); };
  const pickTab = (t: Tab) => { setTab(t); setQuery(''); setActive(0); if (listRef.current) listRef.current.scrollTop = 0; };
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {   // abas: setas movem e ativam (foco rotativo)
    const i = tabs.findIndex((t) => t.id === cur);
    const to = e.key === 'ArrowRight' ? (i + 1) % tabs.length : e.key === 'ArrowLeft' ? (i - 1 + tabs.length) % tabs.length : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : -1;
    if (to < 0) return;
    e.preventDefault(); pickTab(tabs[to].id);
    panelRef.current?.querySelector<HTMLElement>(`#mp-tab-${tabs[to].id}`)?.focus();
  };

  // mantém o item ativo à vista; na abertura centraliza o modelo atual
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-i="${active}"]`);
    el?.scrollIntoView({ block: centerRef.current ? 'center' : 'nearest' });
    centerRef.current = false;
  }, [open, active, rows, devRows]);

  const onSearch = (v: string) => { setQuery(v); setActive(0); if (listRef.current) listRef.current.scrollTop = 0; };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (e.key === 'Tab') { // prende o foco dentro do modal
      const f = panelRef.current?.querySelectorAll<HTMLElement>('input, button:not([tabindex="-1"])');
      if (!f?.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if (!navRows.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(navRows.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'PageDown') { e.preventDefault(); setActive((a) => Math.min(navRows.length - 1, a + 8)); }
    else if (e.key === 'PageUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 8)); }
    else if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); if (cur === 'dev') pickDev(devRows[active]); else activate(rows[active]); }
  };

  // O botão "Ver mais" não pode ficar dentro de um listbox (ARIA: só option/group), então as opções ficam em até dois
  // listbox irmãos, um de cada lado do botão; `.mp-list` é só o contêiner de rolagem. Sem opção não há listbox.
  const renderSections = (id: string, name: string, secs: Section[]) => {
    if (!secs.length) return null;
    const body = secs.map((s) => (
      <div key={s.key} className={`mp-sec${s.key === 'izze' ? ' mp-lead' : ''}`} role={s.title && s.rows.length ? 'group' : 'presentation'} aria-labelledby={s.title && s.rows.length ? `mp-h-${s.key}` : undefined}>
        {s.title && <div className="mp-group" id={`mp-h-${s.key}`}>{s.title}</div>}
        {s.rows.map((r, k) => {
          const i = s.start + k, it = r.item;
          return (
            <div
              key={it.id} id={`mp-opt-${i}`} data-i={i} role="option" aria-selected={it.id === value}
              className={`mp-opt${i === active ? ' active' : ''}${it.id === value ? ' sel' : ''}`}
              onMouseMove={() => { if (active !== i) setActive(i); }} onClick={() => pick(it)}
            >
              <span className="mp-name">{it.name}</span>
              {it.id === '' && <span className="mp-hint">{IZZE_HINT}</span>}
              {it.provider && <span className="mp-prov">{it.provider}</span>}
              {it.vision && <span className="pill">visão</span>}
              {it.id === value && <Check className="mp-check" />}
            </div>
          );
        })}
        {s.empty && <div className="mp-empty" role="presentation">{s.empty}</div>}
      </div>
    ));
    return hasOpt(secs) ? <div id={id} role="listbox" aria-label={name}>{body}</div> : <div>{body}</div>;
  };
  const controls = [hasOpt(head) && 'mp-listbox-1', hasOpt(tail) && 'mp-listbox-2'].filter((x): x is string => Boolean(x));

  // ---- aba Modo Turbo: uma linha por família, três chips (Small / Medium / High)
  const renderTurbo = () => {
    if (!fams.length) {
      return <div className="mp-none"><b>{q ? 'Nenhuma família encontrada' : 'Nenhuma faixa disponível neste escopo'}</b><span>{q ? `Nada corresponde a “${query.trim()}”.` : 'Nenhum modelo com tamanho no nome nas máquinas deste escopo.'}</span></div>;
    }
    return fams.map((f) => (
      <div key={f.family} className="mp-fam" role="group" aria-label={f.family}>
        <span className="mp-fam-name" title={f.family}>{f.family}</span>
        <div className="mp-chips">
          {BAND_ORDER.map((id) => {
            const b = f.bands.find((x) => x.id === id), ok = Boolean(b?.available), star = ok && f.recommendedBand === id, sel = value === bandValue(f.family, id);
            return (
              <button
                key={id} type="button" className={`mp-chip${sel ? ' sel' : ''}`} aria-pressed={sel} aria-disabled={!ok || undefined}
                title={ok ? `${b?.models.join(', ')}${star && b?.recommended ? ` · mais rápido: ${b.recommended}` : ''}` : 'nenhum modelo desta faixa neste escopo'}
                onClick={() => pickBand(f, id)}
              >
                <span className="mp-chip-t">{LABELS.bands[id]}{star && <><span className="mp-star" aria-hidden="true">★</span><span className="sr-only"> recomendado</span></>}</span>
                <span className="mp-chip-n">{ok && b ? machines(b.machines) : '—'}</span>
              </button>
            );
          })}
        </div>
      </div>
    ));
  };

  // ---- aba Modo Desenvolvedor: o que está instalado nas máquinas do escopo; avançado acrescenta velocidade, contexto, preço e máquinas
  const renderDev = () => {
    if (!installed) {
      return models.loading
        ? <div className="mp-skel" aria-busy="true" aria-label="Carregando modelos instalados"><div className="skel" /><div className="skel" /><div className="skel" /></div>
        : <div className="mp-state" role="alert"><b>Não foi possível carregar os modelos instalados</b>{models.error && <span>{models.error}</span>}<button type="button" className="btn sm" onClick={models.reload}>Tentar de novo</button></div>;
    }
    if (!installed.length) return <div className="mp-none"><b>Nenhum modelo instalado</b><span>{kind === 'onprem' ? 'Nenhum modelo instalado nas máquinas desta pool — conecte e prepare as suas em Workers.' : 'Nenhum modelo instalado nesta pool.'}</span></div>;
    if (!devRows.length) {
      return (
        <div className="mp-state">
          <b>{q ? 'Nenhum modelo encontrado' : 'Nenhum modelo recomendado'}</b>
          {q ? <span>Nada corresponde a “{query.trim()}”.</span> : <><span>Nenhum modelo passou no benchmark das máquinas deste escopo.</span><button type="button" className="btn sm" onClick={() => setOnlyOk(false)}>Ver todos</button></>}
        </div>
      );
    }
    return (
      <div className="mp-dev-x">
        <div className={`mp-dev-in${adv ? ' adv' : ''}`}>
          {adv && (
            <div className="mp-dev-h" aria-hidden="true">
              <span>modelo</span><span>tamanho</span><span /><span>tok/s</span><span>contexto</span><span>máx. tokens</span><span>preço / 1M (entrada · saída)</span><span>máquinas</span>
            </div>
          )}
          <div id="mp-listbox-dev" role="listbox" aria-label="Modelos instalados">
            {devRows.map((m, i) => (
              <div
                key={m.id} id={`mp-opt-${i}`} data-i={i} role="option" aria-selected={m.id === value} aria-disabled={!m.qualified || undefined}
                className={`mp-dev-row${i === active ? ' active' : ''}${m.id === value ? ' sel' : ''}${m.qualified ? '' : ' off'}`}
                onMouseMove={() => { if (active !== i) setActive(i); }} onClick={() => pickDev(m)}
              >
                <span className="mp-dev-name"><span className="mp-name">{m.id}{m.vision && <span className="pill">visão</span>}</span>{!m.qualified && m.reason && <span className="mp-why">{m.reason}</span>}</span>
                <span className="mp-dev-cell">{fmtSize(m.size)}</span>
                <span className="mp-dev-cell">{m.recommended && <><span className="mp-star" aria-hidden="true" title="mais rápido medido">★</span><span className="sr-only">recomendado</span></>}</span>
                {adv && (
                  <>
                    <span className="mp-dev-cell">{m.tps != null ? `${Math.round(m.tps)} tok/s` : '—'}</span>
                    <span className="mp-dev-cell">{int(m.numCtx)}</span>
                    <span className="mp-dev-cell">{int(m.maxTokens)}</span>
                    <span className="mp-dev-cell">{brl(m.chargeIn)} · {brl(m.chargeOut)}</span>
                    <span className="mp-dev-cell">{machines(m.machines)}</span>
                  </>
                )}
                {m.id === value && <Check className="mp-check" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const label = bandLabel(value) ?? (current ? (current.provider ? `${current.provider}: ${current.name}` : current.name) : blocked && value ? value : LABELS.izze);
  const listboxId = cur === 'dev' ? 'mp-listbox-dev' : controls.join(' ') || undefined;
  const combo = cur !== 'turbo';   // na aba Turbo as opções são botões (chips): a busca é um campo comum

  return (
    <>
      <button ref={triggerRef} type="button" className="mp-trigger" title="Escolher modelo" aria-haspopup="dialog" aria-expanded={open} onClick={show}>
        <span>{label}</span>
        <ChevronDown />
      </button>

      {open && createPortal(
        <div className="mp-overlay" onMouseDown={onOverlayDown}>
          <div className="mp-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label="Escolher modelo" onKeyDown={onKey}>
            <div className="mp-head">
              <h2>Escolher modelo</h2>
              <button type="button" className="mp-x" onClick={close} aria-label="Fechar"><X /></button>
            </div>

            {tabs.length > 1 && (
              <div className="mp-tabs" role="tablist" aria-label="Como escolher o modelo" onKeyDown={onTabKey}>
                {tabs.map((t) => (
                  <button key={t.id} id={`mp-tab-${t.id}`} type="button" role="tab" className="mp-tab" aria-selected={t.id === cur} aria-controls="mp-tabpanel" tabIndex={t.id === cur ? 0 : -1} onClick={() => pickTab(t.id)}>{t.label}</button>
                ))}
              </div>
            )}

            <div className="mp-body" id="mp-tabpanel" role={tabs.length > 1 ? 'tabpanel' : undefined} aria-labelledby={tabs.length > 1 ? `mp-tab-${cur}` : undefined}>
              <div className="mp-search">
                <Search />
                <input
                  autoFocus type="text" value={query} onChange={(e) => onSearch(e.target.value)}
                  placeholder={cur === 'turbo' ? 'Buscar família…' : 'Buscar modelo…'} aria-label={cur === 'turbo' ? 'Buscar família' : 'Buscar modelo'} autoComplete="off" spellCheck={false}
                  role={combo ? 'combobox' : undefined} aria-expanded={combo ? (cur === 'dev' ? devRows.length > 0 : controls.length > 0) : undefined}
                  aria-controls={combo ? listboxId : undefined} aria-activedescendant={combo && navRows[active] ? `mp-opt-${active}` : undefined}
                />
                {query && <button type="button" className="mp-clear" tabIndex={-1} onClick={() => { onSearch(''); panelRef.current?.querySelector('input')?.focus(); }} aria-label="Limpar busca"><X /></button>}
              </div>

              {cur === 'dev' && (
                <div className="mp-dev-bar">
                  <div className="mp-seg" role="group" aria-label="Quais modelos">
                    <button type="button" aria-pressed={onlyOk} onClick={() => { setOnlyOk(true); setActive(0); }}>Recomendados</button>
                    <button type="button" aria-pressed={!onlyOk} onClick={() => { setOnlyOk(false); setActive(0); }}>Todos</button>
                  </div>
                  <div className="mp-seg" role="group" aria-label="Nível de detalhe">
                    <button type="button" aria-pressed={!adv} onClick={() => setAdv(false)}>Simples</button>
                    <button type="button" aria-pressed={adv} onClick={() => setAdv(true)}>Avançado</button>
                  </div>
                </div>
              )}

              {/* tabIndex 0: sem role de listbox, a região rolável precisa ser focável (axe scrollable-region-focusable). A armadilha
                  de Tab só considera input e botões, então este foco não entra na ordem de Tab; clicar num vazio da lista foca a lista
                  (dentro do painel) em vez do <body>. */}
              <div className="mp-list" ref={listRef} tabIndex={0}>
                {cur === 'default' && (
                  <>
                    {rows.length === 0 && (
                      <div className="mp-none"><b>Nenhum modelo encontrado</b><span>Nada corresponde a “{query.trim()}”.</span></div>
                    )}
                    {renderSections('mp-listbox-1', 'Modelos', head)}
                    {moreIdx >= 0 && (
                      <button
                        id={`mp-opt-${moreIdx}`} data-i={moreIdx} type="button" tabIndex={-1} aria-expanded={expanded}
                        className={`mp-more${moreIdx === active ? ' active' : ''}`}
                        onMouseDown={(e) => e.preventDefault()} onMouseMove={() => { if (active !== moreIdx) setActive(moreIdx); }} onClick={toggleMore}
                      >
                        <span>Ver mais</span>
                        <ChevronDown />
                      </button>
                    )}
                    {renderSections('mp-listbox-2', 'Mais modelos', tail)}
                    {blocked && (models.error
                      ? <div className="mp-state" role="alert"><b>Não foi possível carregar os modelos deste escopo</b><span>{models.error}</span><button type="button" className="btn sm" onClick={models.reload}>Tentar de novo</button></div>
                      : <div className="mp-skel" aria-busy="true" aria-label="Carregando modelos"><div className="skel" /><div className="skel" /><div className="skel" /></div>)}
                  </>
                )}
                {cur === 'turbo' && (!data   // as faixas só vêm do servidor (não há lista provisória como na aba Padrão)
                  ? (models.error
                    ? <div className="mp-state" role="alert"><b>Não foi possível carregar as faixas</b><span>{models.error}</span><button type="button" className="btn sm" onClick={models.reload}>Tentar de novo</button></div>
                    : <div className="mp-skel" aria-busy="true" aria-label="Carregando faixas"><div className="skel" /><div className="skel" /><div className="skel" /></div>)
                  : renderTurbo())}
                {cur === 'dev' && renderDev()}
              </div>

              <div className="mp-foot">
                <span>
                  {cur === 'turbo' ? `${fams.length} ${fams.length === 1 ? 'família' : 'famílias'} · ★ mais rápido medido`
                    : cur === 'dev' ? `${devRows.length} de ${installed?.length ?? 0} instalados`
                    : query.trim() ? `${rows.length} de ${items.length} ${noun(items.length)}` : `${items.length} ${noun(items.length)}`}
                </span>
                <span className="mp-keys"><kbd>↑</kbd><kbd>↓</kbd> navegar · <kbd>enter</kbd> escolher · <kbd>esc</kbd> fechar</span>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
